import { supabase } from './supabase';
import fs from 'fs';
import path from 'path';

// Función para corregir la estructura de la base de datos
export const fixDatabase = async (): Promise<boolean> => {
  try {
    console.log('Corrigiendo estructura de la base de datos...');
    
    // Verificar si la tabla user_coins existe
    const { data: userCoinsExists, error: userCoinsError } = await supabase
      .from('user_coins')
      .select('id')
      .limit(1);
    
    // Si hay un error, probablemente la tabla no existe
    if (userCoinsError && userCoinsError.code === '42P01') {
      console.log('La tabla user_coins no existe, creándola...');
      
      // Ejecutar SQL para crear la tabla user_coins
      const { error: createError } = await supabase.rpc('create_transactions_table');
      
      if (createError) {
        console.error('Error al crear tablas:', createError);
        return false;
      }
      
      console.log('Tablas creadas correctamente');
    } else if (userCoinsError) {
      console.error('Error al verificar tabla user_coins:', userCoinsError);
      return false;
    } else {
      console.log('La tabla user_coins ya existe');
      
      // Verificar si la columna balance existe
      try {
        const { data, error } = await supabase
          .from('user_coins')
          .select('balance')
          .limit(1);
        
        if (error && error.code === '42703') {
          console.log('La columna balance no existe, agregándola...');
          
          // Intentar agregar la columna balance directamente
          try {
            const { error: alterError } = await supabase.rpc('add_balance_column');
            
            if (alterError) {
              console.error('Error al agregar columna balance:', alterError);
              return false;
            }
            
            console.log('Columna balance agregada correctamente');
          } catch (alterError) {
            console.error('Error al agregar columna balance:', alterError);
            return false;
          }
        } else if (error) {
          console.error('Error al verificar columna balance:', error);
          return false;
        } else {
          console.log('La columna balance ya existe');
        }
        
        // Verificar si la columna coins existe
        const { data: coinsData, error: coinsError } = await supabase
          .from('user_coins')
          .select('coins')
          .limit(1);
        
        if (coinsError && coinsError.code === '42703') {
          console.log('La columna coins no existe, agregándola...');
          
          // Intentar agregar la columna coins directamente
          try {
            // Crear una función SQL para agregar la columna coins
            const addCoinsColumnSQL = `
              DO $$
              BEGIN
                IF NOT EXISTS (
                  SELECT FROM information_schema.columns 
                  WHERE table_schema = 'public' 
                  AND table_name = 'user_coins' 
                  AND column_name = 'coins'
                ) THEN
                  ALTER TABLE public.user_coins ADD COLUMN coins INTEGER NOT NULL DEFAULT 0;
                  
                  -- Copiar los valores de balance a coins
                  UPDATE public.user_coins SET coins = balance;
                END IF;
              END;
              $$;
            `;
            
            // Ejecutar el SQL directamente
            const { error: alterError } = await supabase.rpc('exec_sql', { sql: addCoinsColumnSQL });
            
            if (alterError) {
              console.error('Error al agregar columna coins:', alterError);
              // No fallamos si no podemos agregar la columna coins
            } else {
              console.log('Columna coins agregada correctamente');
            }
          } catch (alterError) {
            console.error('Error al agregar columna coins:', alterError);
            // No fallamos si no podemos agregar la columna coins
          }
        } else if (coinsError) {
          console.error('Error al verificar columna coins:', coinsError);
          // No fallamos si no podemos verificar la columna coins
        } else {
          console.log('La columna coins ya existe');
          
          // Sincronizar los valores de balance y coins donde coins sea NULL
          try {
            // 1. Encontrar filas donde coins es NULL pero balance tiene valor
            const { data: rowsToSync, error: selectError } = await supabase
              .from('user_coins')
              .select('id, balance')
              .is('coins', null)
              .not('balance', 'is', null); // Asegurarse que balance no sea null

            if (selectError) {
              console.error('Error al seleccionar filas para sincronizar balance y coins:', selectError);
            } else if (rowsToSync && rowsToSync.length > 0) {
              console.log(`Sincronizando ${rowsToSync.length} filas...`);
              // 2. Actualizar cada fila individualmente (o en batch si es necesario más adelante)
              let updatedCount = 0;
              for (const row of rowsToSync) {
                const { error: updateError } = await supabase
                  .from('user_coins')
                  .update({ coins: row.balance }) // Copiar el valor de balance a coins
                  .eq('id', row.id);
                  
                if (updateError) {
                  console.error(`Error al sincronizar fila ${row.id}:`, updateError);
                } else {
                  updatedCount++;
                }
              }
              console.log(`Columnas balance y coins sincronizadas para ${updatedCount} filas.`);
            } else {
              console.log('No hay filas que necesiten sincronización entre balance y coins.');
            }
          } catch (syncError) {
            console.error('Error general al sincronizar columnas balance y coins:', syncError);
          }
        }
      } catch (error) {
        console.error('Error al verificar columnas de user_coins:', error);
        return false;
      }
    }
    
    // Corregir las políticas de seguridad
    await fixSecurityPolicies();
    
    return true;
  } catch (error) {
    console.error('Error al corregir estructura de la base de datos:', error);
    return false;
  }
};

// Función para corregir las políticas de seguridad
export const fixSecurityPolicies = async (): Promise<boolean> => {
  try {
    console.log('Corrigiendo políticas de seguridad...');
    
    // En lugar de intentar ejecutar SQL directamente, vamos a usar la API de Supabase
    // para verificar si podemos acceder a las tablas
    
    // 1. Verificar acceso a coin_history
    console.log('Verificando acceso a coin_history...');
    const { data: coinHistoryData, error: coinHistoryError } = await supabase
      .from('coin_history')
      .select('id')
      .limit(1);
    
    if (coinHistoryError) {
      console.log('Error al acceder a coin_history:', coinHistoryError);
    } else {
      console.log('Acceso a coin_history correcto');
    }
    
    // 2. Verificar acceso a transactions
    console.log('Verificando acceso a transactions...');
    const { data: transactionsData, error: transactionsError } = await supabase
      .from('transactions')
      .select('id')
      .limit(1);
    
    if (transactionsError) {
      console.log('Error al acceder a transactions:', transactionsError);
    } else {
      console.log('Acceso a transactions correcto');
    }
    
    // 3. Verificar acceso a user_coins
    console.log('Verificando acceso a user_coins...');
    const { data: userCoinsData, error: userCoinsError } = await supabase
      .from('user_coins')
      .select('id')
      .limit(1);
    
    if (userCoinsError) {
      console.log('Error al acceder a user_coins:', userCoinsError);
    } else {
      console.log('Acceso a user_coins correcto');
    }
    
    // 4. Intentar insertar un registro de prueba en coin_history y luego eliminarlo
    // Esto nos ayudará a verificar si tenemos permisos de escritura
    if (coinHistoryError && coinHistoryError.code === '42501') {
      console.log('Intentando corregir permisos de coin_history mediante inserción de prueba...');
      
      // Obtener el usuario actual
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Intentar insertar un registro de prueba
        const { data: insertData, error: insertError } = await supabase
          .from('coin_history')
          .insert([
            {
              user_id: user.id,
              amount: 0,
              type: 'test',
              description: 'Prueba de permisos'
            }
          ])
          .select();
        
        if (insertError) {
          console.log('Error al insertar registro de prueba en coin_history:', insertError);
        } else if (insertData && insertData.length > 0) {
          console.log('Inserción de prueba en coin_history exitosa');
          
          // Eliminar el registro de prueba
          const { error: deleteError } = await supabase
            .from('coin_history')
            .delete()
            .eq('id', insertData[0].id);
          
          if (deleteError) {
            console.log('Error al eliminar registro de prueba en coin_history:', deleteError);
          } else {
            console.log('Eliminación de registro de prueba en coin_history exitosa');
          }
        }
      }
    }
    
    console.log('Verificación de políticas de seguridad completada');
    return true;
  } catch (error) {
    console.error('Error al corregir políticas de seguridad:', error);
    return false;
  }
};

// Función para crear una función RPC en Supabase para agregar la columna balance
export const createAddBalanceColumnFunction = async (): Promise<boolean> => {
  try {
    console.log('Creando función para agregar columna balance...');
    
    const sql = `
      CREATE OR REPLACE FUNCTION public.add_balance_column()
      RETURNS void
      LANGUAGE plpgsql
      SECURITY DEFINER
      AS $$
      BEGIN
        -- Verificar si la columna balance existe
        IF NOT EXISTS (
          SELECT FROM information_schema.columns 
          WHERE table_schema = 'public' 
          AND table_name = 'user_coins' 
          AND column_name = 'balance'
        ) THEN
          -- Agregar la columna balance
          ALTER TABLE public.user_coins ADD COLUMN balance INTEGER NOT NULL DEFAULT 0;
        END IF;
      END;
      $$;
    `;
    
    const { error } = await supabase.rpc('exec_sql', { sql });
    
    if (error) {
      console.error('Error al crear función add_balance_column:', error);
      return false;
    }
    
    console.log('Función add_balance_column creada correctamente');
    return true;
  } catch (error) {
    console.error('Error al crear función add_balance_column:', error);
    return false;
  }
}; 